import { useTheme } from "@certn/cascadia/context/ThemeContext";
import {
  CascadiaColor,
  CascadiaShadow,
  Shadow,
  breakpoints as bpObject,
  darkShadows,
  lightPresetStyle,
  lightShadows,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import facepaint, { DynamicStyle } from "facepaint";

import { useColorScheme } from "../hooks/useColorScheme";
import { useColors } from "../hooks/useColors";
import { usePresetStyles } from "../hooks/usePresetSyles";

export function useFocusStyle() {
  const presetStyles = usePresetStyles();

  return (style: keyof typeof lightPresetStyle) =>
    css({ "&:focus": css({ outline: "0", boxShadow: presetStyles[style] }) });
}

const lightBoxShadows = Object.keys(lightShadows).reduce<
  Record<string, string>
>((prev, key) => {
  return {
    ...prev,
    [key]: getShadows(lightShadows[key as CascadiaShadow]),
  };
}, {});

const darkBoxShadows = Object.keys(darkShadows).reduce<Record<string, string>>(
  (prev, key) => {
    return {
      ...prev,
      [key]: getShadows(darkShadows[key as CascadiaShadow]),
    };
  },
  {}
);

export function useShadows(shadow?: keyof typeof lightShadows) {
  const { theme = "system" } = useTheme();
  const systemTheme = useColorScheme() || "light";
  const scheme = theme === "system" ? systemTheme : theme;

  return scheme === "light" ? lightBoxShadows : darkBoxShadows;
}

export function getShadows(token: Shadow[]) {
  return token
    .map((token) => {
      return [
        `${token.x}px`,
        `${token.y}px`,
        `${token.blur}px`,
        `${token.spread}px`,
        `${token.color}`,
        token.inset && "inset",
      ]
        .filter(Boolean)
        .join(" ");
    })
    .join(",");
}

export function useGetAllFocusShadows() {
  const focusShadow = "var(--focus-action)";

  // in the cases of borderless buttons, we'll just display focus shadows
  return (innerShadow?: string) => {
    if (innerShadow)
      return css({
        boxShadow: [
          focusShadow, // white and blue outer rings
          innerShadow, // usually grey inset shadow, see ToggleCards
        ].join(),
      });
    return css({ boxShadow: focusShadow });
  };
}

/**
 * Allows the use of breakpoint specific CSS using facepaint.
 * @example // 100% for breakpoint sm, 50% for breakpoint md and above
 * mq({
 *   width: ["100%", "50%"],
 * })
 * The last item in each array will be used in print layouts.
 */
export function mq(styles: object | object[]): DynamicStyle[] {
  const screenQueries = facepaint(
    Object.values(bpObject).map(
      (bp) => `@media screen and (min-width: ${bp.maxWidth}px)`
    )
  )(styles);

  const printProperties = Object.entries(styles).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: Array.isArray(value) ? value.at(-1) : value,
    }),
    {}
  );

  return [...screenQueries, { "@media print": printProperties }];
}

/**
 * @deprecated Breakpoint tokens are no longer dynamic which means this hook is no longer needed. You can import `mq` instead, which works in the same way as `mediaQuery`.
 */
export function useMediaQuery() {
  const breakpoints = Object.values(bpObject);

  return {
    mediaQuery: mq,
    breakpoints,
  };
}

interface boxShadowBorderTypes {
  type?: "top" | "bottom" | "both" | "right" | undefined;
  color?: CascadiaColor;
}

export function useBoxShadowBorder({
  type,
  color = "border-base",
}: boxShadowBorderTypes = {}) {
  const colors = useColors();
  switch (type) {
    case "top":
      return css({
        boxShadow: `0px -1px 0px 0px ${colors[color]}`,
      });
    case "bottom":
      return css({
        boxShadow: `0px 1px 0px 0px ${colors[color]}`,
      });
    case "right":
      return css({
        boxShadow: `1px 0px 1px 0px ${colors[color]}`,
      });
    default:
      // full border, top right bottom left
      return css({ boxShadow: `0 0 0 1px ${colors[color]}` });
  }
}

const MIN_TOUCHABLE_SIZE = 44;

export const accessibleTouchSize = css({
  position: "absolute",
  content: '""',
  top: "50%",
  bottom: "50%",
  left: "50%",
  right: "50%",
  width: "100%",
  height: "100%",
  minHeight: MIN_TOUCHABLE_SIZE,
  minWidth: MIN_TOUCHABLE_SIZE,
  transform: "translate(-50%, -50%)",
});
